import { Control, Controller } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import useConsiderationCalculations, {
  ConsiderationPerUnitType,
  ConsiderationTotalType,
} from '../../hooks/useConsiderationCalculations';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { InputAdornment, Typography } from '@mui/material';

type Props = {
  control: Control<rules.CreateRealisationFormValues>;
  index: number;
  currencyCode: CurrencyCode;
  disabled: boolean;
};

const ManualEscrowConsiderationRow: React.FC<Props> = ({
  control,
  index,
  currencyCode,
  disabled,
}) => {
  const { onManualConsiderationChange, setFieldFocus } =
    useConsiderationCalculations();

  return (
    <FormField
      label="Escrow consideration"
      labelSize="sm"
      tooltip="This is the total consideration placed in escrow and not yet received by the fund following the realisation but to which the fund has a future right. Any escrow consideration physically received should be removed from this row and added to the received consideration total."
      layout="horizontal"
      breakpoint="md"
    >
      <Row spacing="sm">
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.escrowConsiderationPerUnit`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={10}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationPerUnitType.Escrow,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationPerUnitType.Escrow);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Per unit</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.escrowConsiderationTotal`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={2}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationTotalType.Escrow,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationTotalType.Escrow);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Total</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Row>
    </FormField>
  );
};

export default ManualEscrowConsiderationRow;
